import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { ModalTitle } from '@components/modal-title/modal-title';
import { NotifyService } from '@intelishs-group/notify-service';
import { ProspectService } from '@services/prospect-service';
import { FileManagerService } from '@services/filemanager-service';
import { FileViewerService } from '@intelishs-group/ui';
import type { JsonFilesEntity, Prospect } from '@schemas/prospect';
import { environment } from 'src/environments/environment';

import { DOCUMENT_TYPES, type DocumentTypeDef } from './document-types';

interface DocumentSlot {
  fileName: string | null;
  uploading: boolean;
  viewing: boolean;
  kiddocumento: number | null;
}

function emptySlot(): DocumentSlot {
  return { fileName: null, uploading: false, viewing: false, kiddocumento: null };
}

// La Constancia Fiscal puede llegar pre-cargada desde el alta del prospecto vía CIF;
// no debe contar como "ya tenía documentos" para el avance de etapa desde paso 4.
const CONSTANCIA_FISCAL_TYPE_ID = 2;
const isUserManagedDoc = (a: JsonFilesEntity) => a.tipo !== CONSTANCIA_FISCAL_TYPE_ID;

@Component({
  selector: 'app-prospect-documents-modal',
  standalone: true,
  imports: [
    NzModalModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    NzTooltipModule,
    NzPopconfirmModule,
    ModalTitle,
  ],
  templateUrl: './prospect-documents-modal.html',
  styleUrl: './prospect-documents-modal.css',
})
export class ProspectDocumentsModal implements OnChanges {
  private prospectService = inject(ProspectService);
  private fileManagerService = inject(FileManagerService);
  private fileViewerService = inject(FileViewerService);
  private notifyService = inject(NotifyService);
  private http = inject(HttpClient);

  @Input() visible = false;
  @Input() prospectId = 0;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() uploaded = new EventEmitter<boolean>();

  readonly columns = [1, 2, 3] as const;
  readonly documentTypes = DOCUMENT_TYPES.filter(d => d.enabled);

  slots = new Map<number, DocumentSlot>();
  loading = false;

  private prospect: Prospect | null | undefined = null;
  private hasNewUploads = false;
  private hadInitialFiles = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.prospectId) {
      this.hasNewUploads = false;
      this.loadExistingFiles();
    }
  }

  getTypesByColumn(col: number): DocumentTypeDef[] {
    return this.documentTypes.filter(d => d.column === col);
  }

  getSlot(id: number): DocumentSlot {
    let slot = this.slots.get(id);
    if (!slot) {
      slot = emptySlot();
      this.slots.set(id, slot);
    }
    return slot;
  }

  async onFileSelected(event: Event, docType: DocumentTypeDef): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected
    input.value = '';

    const slot = this.getSlot(docType.id);
    slot.uploading = true;
    slot.fileName = file.name;

    try {
      const formData = new FormData();
      formData.append('IdSystem', '1');
      formData.append('IdFileType', docType.id.toString());
      formData.append('File', file, file.name);
      const response = await this.fileManagerService.agregateFiles(formData);
      const pathId = response.object?.pathId;

      if (!pathId) {
        throw new Error('No se recibió el ID del archivo');
      }

      // Update prospect files
      const currentFiles = this.getCurrentFiles();
      // Remove existing file of same type, then add new one
      const updatedFiles = currentFiles.filter(f => f.tipo !== docType.id);
      updatedFiles.push({ tipo: docType.id, kiddocumento: pathId });

      await this.prospectService.updateProspect({
        idProspect: this.prospectId,
        nombre: this.prospect?.nombre ?? null,
        rfc: this.prospect?.rfc ?? null,
        email: this.prospect?.email ?? null,
        datosJson: this.buildDatosJsonForUpdate(updatedFiles),
      });

      slot.kiddocumento = pathId;
      this.notifyService.successNotyfy('Documento subido', `Archivo ${file.name} subido correctamente`);
      this.hasNewUploads = true;
    } catch (error) {
      slot.fileName = null;
      slot.kiddocumento = null;
      this.notifyService.errorNotyfy(`Error al subir el archivo ${file.name}`);
      console.error('Upload error:', error);
    } finally {
      slot.uploading = false;
    }
  }

  async viewFile(event: Event, docType: DocumentTypeDef): Promise<void> {
    event.stopPropagation();

    const slot = this.getSlot(docType.id);
    if (!slot.kiddocumento || slot.viewing || slot.uploading) return;

    slot.viewing = true;

    try {
      const response = await lastValueFrom(
        this.http.get(`${environment.BaseUrl}/FileManager/DownodalFile`, {
          params: { idFile: slot.kiddocumento.toString() },
          responseType: 'blob',
          observe: 'response',
        })
      );
      const blob = response.body!;
      const cd = response.headers.get('Content-Disposition');
      const filenameUtf8 = cd?.match(/filename\*=UTF-8''(.+?)(?:;|$)/i)?.[1];
      const filenameAscii = cd?.match(/filename="?([^";\n]+)"?/)?.[1];
      const serverFilename = filenameUtf8 ? decodeURIComponent(filenameUtf8) : filenameAscii;
      const filename = serverFilename ?? docType.label ?? `Documento_${slot.kiddocumento}`;

      this.fileViewerService.showPreview(blob, filename);
    } catch (error) {
      this.notifyService.errorNotyfy('Error al cargar el documento');
      console.error('View error:', error);
    } finally {
      slot.viewing = false;
    }
  }

  /**
   * Lo invoca el (nzOnConfirm) del popconfirm, así que ya no recibe el evento:
   * cortar la propagación del click es responsabilidad del template.
   */
  async deleteFile(docType: DocumentTypeDef): Promise<void> {
    const slot = this.getSlot(docType.id);
    if (!slot.kiddocumento) {
      // Only exists in UI, just clear
      this.slots.set(docType.id, emptySlot());
      return;
    }

    slot.uploading = true;

    try {
      await this.fileManagerService.deleteFiles(slot.kiddocumento);

      // Update prospect files
      const updatedFiles = this.getCurrentFiles().filter(f => f.tipo !== docType.id);
      await this.prospectService.updateProspect({
        idProspect: this.prospectId,
        nombre: this.prospect?.nombre ?? null,
        rfc: this.prospect?.rfc ?? null,
        email: this.prospect?.email ?? null,
        datosJson: this.buildDatosJsonForUpdate(updatedFiles),
      });

      this.slots.set(docType.id, emptySlot());
      this.notifyService.successNotyfy('Documento eliminado', 'Documento eliminado correctamente');
    } catch (error) {
      slot.uploading = false;
      this.notifyService.errorNotyfy('Error al eliminar el documento');
      console.error('Delete error:', error);
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);

    const hasFilesNow = this.getCurrentFiles().some(isUserManagedDoc);
    const isFirstUpload = !this.hadInitialFiles && hasFilesNow;
    const hadAnyChange = this.hasNewUploads || this.hadInitialFiles !== hasFilesNow;

    if (hadAnyChange) {
      this.uploaded.emit(isFirstUpload);
    }
  }

  // --- Private helpers ---

  private async loadExistingFiles(): Promise<void> {
    this.slots.clear();
    this.loading = true;

    try {
      this.prospect = await this.prospectService.byId(this.prospectId);
      const archivos = this.prospect?.datosJson?.archivos ?? [];
      this.hadInitialFiles = archivos.some(isUserManagedDoc);

      for (const archivo of archivos) {
        if (archivo.tipo != null && archivo.kiddocumento != null) {
          const def = this.documentTypes.find(d => d.id === archivo.tipo);
          this.slots.set(archivo.tipo, {
            fileName: def?.label ?? 'Documento',
            uploading: false,
            viewing: false,
            kiddocumento: archivo.kiddocumento,
          });
        }
      }
    } catch (error) {
      this.notifyService.errorNotyfy('Error al cargar los documentos del prospecto');
      console.error('Load error:', error);
    } finally {
      this.loading = false;
    }
  }

  private getCurrentFiles(): JsonFilesEntity[] {
    const files: JsonFilesEntity[] = [];
    for (const [tipo, slot] of this.slots) {
      if (slot.kiddocumento != null) {
        files.push({ tipo, kiddocumento: slot.kiddocumento });
      }
    }
    return files;
  }

  /**
   * Construye el datosJson para el request de updateProspect.
   * El DTO de respuesta usa fkidbroker/fkidimplant/ncomisionporcentaje en vendedores[];
   * el de request espera idBroker/idImplant/comisionPorcentaje. Se mapea aquí. prospecto y
   * spropuesta son idénticos entre request y response, así que se pasan tal cual.
   */
  private buildDatosJsonForUpdate(archivos: JsonFilesEntity[]) {
    const d = this.prospect?.datosJson;
    const c = d?.contacto;
    const writeVendedores = (d?.vendedores ?? []).map((v) => ({
      idBroker: v.fkidbroker ?? 0,
      idImplant: v.fkidimplant ?? 0,
      comisionPorcentaje: v.ncomisionporcentaje ?? 0,
    }));
    return {
      ...(d?.prospecto ? { prospecto: d.prospecto } : {}),
      ...(c ? {
        contacto: {
          snombre: c.snombre ?? null,
          itelefono: c.itelefono ?? null,
          scorreo: c.scorreo ?? null,
        },
      } : {}),
      ...(d?.spropuesta ? { spropuesta: d.spropuesta } : {}),
      ...(writeVendedores.length > 0 ? { vendedores: writeVendedores } : {}),
      archivos,
    };
  }
}
